import {
  faBook,
  faBrain,
  faDumbbell,
  faHeart,
  faPersonRunning,
  faSmile,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { faEclipse, faMoonStars, faSun } from "@fortawesome/pro-light-svg-icons"
import { faSunrise } from "@fortawesome/pro-light-svg-icons"
import { QuestInstance, QuestRoutine, SkillKey } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { RRule } from "rrule"

import { ScheduledMessageWithRoutine } from "../types/scheduledMessages"

/**
 * Returns the icon and text associated with a given quest category.
 * @param category - The category of the quest as a SkillKey or null.
 * @returns An object containing the icon and text for the category.
 */
export const getQuestCategoryIconAndText = (category: SkillKey | null) => {
  switch (category) {
    case SkillKey.FOCUS:
      return { icon: faBrain, text: "Focus" }
    case SkillKey.KINDNESS:
      return { icon: faHeart, text: "Kindness" }
    case SkillKey.KNOWLEDGE:
      return { icon: faBook, text: "Knowledge" }
    case SkillKey.STAMINA:
      return { icon: faPersonRunning, text: "Stamina" }
    case SkillKey.STRENGTH:
      return { icon: faDumbbell, text: "Strength" }
    case SkillKey.TEAMWORK:
      return { icon: faUsers, text: "Teamwork" }
    default:
      return { icon: faSmile, text: "Other" }
  }
}

export const getRoutineIconAndText = (routine: QuestRoutine) => {
  switch (routine) {
    case QuestRoutine.MORNING:
      return { icon: faSunrise, text: "Morning" }
    case QuestRoutine.AFTERNOON:
      return { icon: faSun, text: "Afternoon" }
    case QuestRoutine.NIGHT:
      return { icon: faMoonStars, text: "Night" }
    case QuestRoutine.ANYTIME:
    default:
      return { icon: faEclipse, text: "Anytime" }
  }
}

/**
 * Generates a repetition text based on the repetition pattern and frequency.
 * @param repetition - An array indicating the days of repetition.
 * @param repeatsFrequency - The frequency of repetition in weeks.
 * @returns A string describing the repetition pattern.
 */
export const getRepetitionText = (
  repetition: boolean[] | null | undefined,
  repeatsFrequency: number | null | undefined
) => {
  if (repetition?.every((val) => !!val)) {
    if (!repeatsFrequency || repeatsFrequency === 1) {
      return "Repeats every day"
    } else if (repeatsFrequency > 1) {
      return `Repeats daily every ${repeatsFrequency} weeks`
    }
  }
  let text = "Repeats"

  if (repetition) {
    const days = getDayAbbreviationsDDD(repetition)
    if (Array.isArray(days)) {
      text += ` on ${days.join(", ")}`
    } else if (days) {
      text += ` on ${days}`
    } else {
      return "Does not repeat"
    }
  }
  return text
}

/**
 * Converts a repetition array into day abbreviations.
 * @param repetitionArray - An array indicating the days of repetition.
 * @returns An array of day abbreviations or an empty string if no days are selected.
 */
export const getDayAbbreviationsDDD = (repetitionArray: boolean[]) => {
  if (repetitionArray.every((val) => !val)) return ""
  if (repetitionArray.filter((val) => !!val).length === 1)
    return dayjs().day(repetitionArray.indexOf(true)).format("ddd")
  const allDays = repetitionArray.map((day: boolean, i: number) =>
    day ? dayjs().day(i).format("ddd") : ""
  )
  return allDays.filter((day) => !!day)
}

/**
 * Extracts the repetition interval from an RRule string.
 * @param rruleString - The RRule string.
 * @returns The interval of repetition.
 */
export const getRepetitionIntervalFromRrule = (rruleString: string): number => {
  return RRule.fromString(rruleString).options.interval
}

/**
 * Groups quests by their quest group ID.
 * @param quests - An array of QuestInstance objects.
 * @returns An array of quest groups.
 */
const groupQuestsByQuestGroupId = (quests: QuestInstance[]) => {
  return Object.values(
    quests.reduce((acc, quest) => {
      const groupId = quest.series.quest_group_id ?? `ungrouped-${quest.id}` // Use a unique key for ungrouped quests
      acc[groupId] = [...(acc[groupId] || []), quest]
      return acc
    }, {} as Record<string | number, QuestInstance[]>)
  )
}

/**
 * Groups quest groups by their routine.
 * @param questGroups - An array of quest groups.
 * @returns A record of quest groups organized by routine.
 */
const groupQuestGroupsByRoutine = (questGroups: QuestInstance[][]) => {
  return questGroups.reduce((acc, questGroup) => {
    acc[questGroup[0].series.routine] = {
      questGroups: [
        ...(acc[questGroup[0].series.routine]?.questGroups || []),
        questGroup,
      ],
    }
    return acc
  }, {} as Record<QuestRoutine, { questGroups: QuestInstance[][] }>)
}

/**
 * Adds scheduled messages to routines.
 * @param questGroupsByRoutine - A record of quest groups organized by routine.
 * @param scheduledMessages - An array of scheduled messages.
 * @returns A record of routines with added scheduled messages.
 */
const addScheduledMessagesToRoutines = (
  questGroupsByRoutine: Record<
    QuestRoutine,
    { questGroups: QuestInstance[][] }
  >,
  scheduledMessages: ScheduledMessageWithRoutine[]
) => {
  const result = { ...questGroupsByRoutine } as Record<
    QuestRoutine,
    {
      questGroups: QuestInstance[][]
      scheduledMessages: ScheduledMessageWithRoutine[]
    }
  >
  scheduledMessages.forEach((message) => {
    const routine = message.routine
    if (!result[routine]?.scheduledMessages) {
      result[routine] = {
        questGroups: questGroupsByRoutine?.[routine]?.questGroups || [],
        scheduledMessages: [],
      }
    }
    result[routine].scheduledMessages.push(message)
  })
  return result
}

/**
 * Orders routines based on a predefined order.
 * @param routines - A record of routines with quest groups and scheduled messages.
 * @returns An ordered array of routines.
 */
const getRoutinesInOrder = (
  routines: Record<
    QuestRoutine,
    {
      questGroups: QuestInstance[][]
      scheduledMessages: ScheduledMessageWithRoutine[]
    }
  >
): {
  questGroups: QuestInstance[][]
  scheduledMessages: ScheduledMessageWithRoutine[]
}[] => {
  const routineOrder = [
    QuestRoutine.MORNING,
    QuestRoutine.AFTERNOON,
    QuestRoutine.NIGHT,
    QuestRoutine.ANYTIME,
  ]

  return routineOrder.map((routine) => routines[routine]).filter(Boolean)
}

/**
 * Combines and sorts quest groups and scheduled messages by time.
 * @param questGroups - An array of quest groups.
 * @param scheduledMessages - An array of scheduled messages.
 * @returns A sorted array of combined items.
 */
const createSortedCombinedItems = (
  questGroups: QuestInstance[][],
  scheduledMessages: ScheduledMessageWithRoutine[]
) => {
  const combinedItems = [
    ...questGroups.map((questGroup) => ({
      type: "questGroup",
      data: questGroup,
      time: questGroup[0].series.reminder_time ?? "00:00",
    })),
    ...(scheduledMessages?.map((message) => ({
      type: "scheduledMessage",
      data: message,
      time: dayjs(message.datetime).format("HH:mm"),
    })) || []),
  ]

  // Convert time strings to minutes since midnight and sort
  combinedItems.sort((a, b) => {
    const timeToMinutes = (time: string) => {
      const [hours, minutes] = time.split(/[: ]/)
      return Number(hours) * 60 + Number(minutes)
    }
    return timeToMinutes(a.time) - timeToMinutes(b.time)
  })

  return combinedItems
}

/**
 * Creates weekly plan sections by combining and sorting quests and scheduled messages.
 * @param quests - An array of QuestInstance objects.
 * @param scheduledMessages - An array of ScheduledMessageWithRoutine objects.
 * @returns An array of sorted combined items for each routine.
 */
export const createWeeklyPlanSections = (
  quests: QuestInstance[],
  scheduledMessages: ScheduledMessageWithRoutine[]
) => {
  const questsGroupedByQuestGroupId = groupQuestsByQuestGroupId(quests)
  const questsGroupsByRoutine = groupQuestGroupsByRoutine(
    questsGroupedByQuestGroupId
  )
  const questsGroupsByRoutineWithScheduledMessages =
    addScheduledMessagesToRoutines(questsGroupsByRoutine, scheduledMessages)
  const routinesInOrder = getRoutinesInOrder(
    questsGroupsByRoutineWithScheduledMessages
  )

  // Create and sort combined items for each routine
  return routinesInOrder.map(({ questGroups, scheduledMessages }) =>
    createSortedCombinedItems(questGroups, scheduledMessages)
  )
}
